import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ExportData } from "./ExportSensorData";
import { Button, Form, Modal, ModalBody, ModalHeader } from "react-bootstrap";
import { DownloadIcon, FileExportIcon, CloseOutlineIcon } from "@components/icons";

function ExportSensor({
  name,
  tree,
  sensors,
  iconOnly = false,
  buttonTitle,
  buttonVariant = "outline-primary",
  buttonClassName = "clickable",
}) {
  const { t } = useTranslation();
  const { auth } = useSelector((state) => state);
  const isPaid = auth?.user?.paid || false;

  const [show, setShow] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [includeMeanValues, setIncludeMeanValues] = useState(false);
  const { todayISO, thirtyDaysAgoISO } = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const start = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    return { todayISO: today, thirtyDaysAgoISO: start };
  }, []);
  const [startDate, setStartDate] = useState(thirtyDaysAgoISO);
  const [endDate, setEndDate] = useState(todayISO);

  const hasPulseSensors = sensors.some((sensor) => sensor.pulse_sensor != null);
  const hasSoilMoistureSensors = sensors.some((sensor) => sensor.soil_moisture_sensor != null);
  const hasSoilWaterTensionSensors = sensors.some(
    (sensor) => sensor.soil_water_tension_sensor != null
  );
  const hasDendrometerSensors = sensors.some((sensor) => sensor.dendrometer_sensor != null);

  const parameters = useMemo(() => {
    const params = [];
    let id = 1;
    const added = new Set();

    const add = (paramName) => {
      if (added.has(paramName)) return;
      added.add(paramName);
      params.push({ id: id++, name: paramName, checked: true, active: true });
    };

    if (hasPulseSensors) {
      add("resistance");
      add("temperature");
      if (isPaid) {
        add("moisture_content");
        add("resis_temp_comp");
      }
    }

    if (hasSoilMoistureSensors) {
      add("water_soil");
      add("temperature");
    }

    if (hasSoilWaterTensionSensors) {
      add("soil_water_tension");
      add("temperature");
    }

    if (hasDendrometerSensors) {
      add("diameter");
    }

    return params;
  }, [
    hasPulseSensors,
    hasSoilMoistureSensors,
    hasSoilWaterTensionSensors,
    hasDendrometerSensors,
    isPaid,
  ]);

  const handleClose = () => {
    setSuccessMessage("");
    setIncludeMeanValues(false);
    setStartDate(thirtyDaysAgoISO);
    setEndDate(todayISO);
    setShow(false);
  };

  const hasParameters = parameters.length > 0;

  return (
    <>
      <Button
        variant={buttonVariant}
        className={buttonClassName}
        onClick={() => setShow(true)}
        title={buttonTitle || t("export")}
        aria-label={buttonTitle || t("export")}
      >
        {!iconOnly && t("export")}
        <FileExportIcon style={iconOnly ? { marginLeft: 0 } : undefined} />
      </Button>

      <Modal onHide={handleClose} show={show} centered>
        <ModalHeader closeButton>{t("download_sensor_data")}</ModalHeader>
        <ModalBody>
          {successMessage && (
            <div className="popup-success">
              <span>{successMessage}</span>
              <CloseOutlineIcon onClick={() => setSuccessMessage("")} />
            </div>
          )}
          <p className="text-muted mb-3">{t("export_sensor_description")}</p>
          <Form className="my-3">
            <div className="mb-3">
              <div className="fw-semibold mb-1">{t("timeframe") || "Timeframe"}</div>
              <div className="d-flex align-items-center gap-2">
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  aria-label="start-date"
                />
                <span>–</span>
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  aria-label="end-date"
                />
              </div>
            </div>
            <Form.Check
              type="checkbox"
              label={t("include_mean_values")}
              checked={includeMeanValues}
              onChange={(e) => setIncludeMeanValues(e.target.checked)}
              className="mb-3"
            />
          </Form>
          {hasParameters ? (
            <ExportData
              tree={tree}
              sensors={sensors}
              fileName={name}
              parameters={parameters}
              setSuccessMessage={setSuccessMessage}
              includeMeanValues={includeMeanValues}
              startDate={startDate}
              endDate={endDate}
            />
          ) : (
            <DisabledButton />
          )}
        </ModalBody>
      </Modal>
    </>
  );
}

const DisabledButton = () => {
  const { t } = useTranslation();
  return (
    <div className="d-flex gap-3 align-items-center ">
      <Button variant="outline-primary d-flex align-items-center" className="disabled">
        <>
          <DownloadIcon />
          {t("download")}
        </>
      </Button>
    </div>
  );
};

export default ExportSensor;
